import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../utils/apiClient";
import { apiData } from "../../utils/apiResult";

const publicBase = () =>
  typeof window !== "undefined" ? window.location.origin : "https://trust.mito.money";

export const getFundingCampaignOptions = createAsyncThunk(
  "FundingCampaign/Options",
  async () => api.get("FundingCampaign/Options")
);

export const createFundingCampaign = createAsyncThunk(
  "FundingCampaign/Create",
  async (data: Record<string, any>) =>
    api.post("FundingCampaign/Create", { data: { ...data, publicBaseUrl: publicBase() } })
);

export const listFundingCampaigns = createAsyncThunk(
  "FundingCampaign/List",
  async () =>
    api.get(`FundingCampaign/List?publicBaseUrl=${encodeURIComponent(publicBase())}`)
);

export const getFundingCampaign = createAsyncThunk(
  "FundingCampaign/Get",
  async (id: string) =>
    api.get(`FundingCampaign/${encodeURIComponent(id)}?publicBaseUrl=${encodeURIComponent(publicBase())}`)
);

export const listFundingCampaignContributions = createAsyncThunk(
  "FundingCampaign/Contributions",
  async (id: string) =>
    api.get(
      `FundingCampaign/${encodeURIComponent(id)}/Contributions?publicBaseUrl=${encodeURIComponent(publicBase())}`
    )
);

export const setFundingCampaignStatus = createAsyncThunk(
  "FundingCampaign/Status",
  async (data: { id: string; status: string }) =>
    api.post("FundingCampaign/Status", { data: { ...data, publicBaseUrl: publicBase() } })
);

export const startFundingCampaignContribution = createAsyncThunk(
  "FundingCampaign/Contribute",
  async (data: { campaignId: string; amount?: number }) =>
    api.post("FundingCampaign/Contribute", { data: { ...data, publicBaseUrl: publicBase() } })
);

export const requestFundingCampaignPayout = createAsyncThunk(
  "FundingCampaign/RequestPayout",
  async (id: string) =>
    api.post("FundingCampaign/RequestPayout", { data: { id, publicBaseUrl: publicBase() } })
);

export const appendFundingCampaignNote = createAsyncThunk(
  "FundingCampaign/AppendNote",
  async (data: { id: string; note: string }) =>
    api.post("FundingCampaign/AppendNote", { data: { ...data, publicBaseUrl: publicBase() } })
);

export const extendFundingCampaign = createAsyncThunk(
  "FundingCampaign/Extend",
  async (data: { id: string; extraDays?: number; expireOnDate?: string; reason: string }) =>
    api.post("FundingCampaign/Extend", { data: { ...data, publicBaseUrl: publicBase() } })
);

export const updateFundingCampaignAmount = createAsyncThunk(
  "FundingCampaign/UpdateAmount",
  async (data: { id: string; targetAmount: number; fixedAmount?: number }) =>
    api.post("FundingCampaign/UpdateAmount", { data: { ...data, publicBaseUrl: publicBase() } })
);

const slice = createSlice({
  name: "fundingCampaign",
  initialState: {
    list: [] as any[],
    current: null as any,
    contributions: [] as any[],
    options: {
      expiryMode: "Days",
      maxExpiryDays: 30,
      expiryDayPresets: [7, 14, 21, 30],
      allowNoEndDate: false,
      allowEndOnTarget: false,
      allowExceedEnabled: false,
      maxExtensions: 1,
      payoutHours: 24,
      payoutMessage: "Paid gifts are already sent to your payment account. Banks usually show them within 24 hours.",
    },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getFundingCampaignOptions.fulfilled, (state, action) => {
        const data = apiData<any>(action.payload) || {};
        const presets = data.expiryDayPresets ?? data.ExpiryDayPresets;
        state.options = {
          expiryMode: String(data.expiryMode ?? data.ExpiryMode ?? "Days"),
          maxExpiryDays: Number(data.maxExpiryDays ?? data.MaxExpiryDays ?? data.expiryDays ?? 30),
          expiryDayPresets: Array.isArray(presets) && presets.length ? presets.map(Number) : [7, 14, 21, 30],
          allowNoEndDate: !!(data.allowNoEndDate ?? data.AllowNoEndDate),
          allowEndOnTarget: !!(data.allowEndOnTarget ?? data.AllowEndOnTarget),
          allowExceedEnabled: !!(data.allowExceedEnabled ?? data.AllowExceedEnabled),
          maxExtensions: Number(data.maxExtensions ?? data.MaxExtensions ?? 1) || 1,
          payoutHours: Number(data.payoutHours ?? data.PayoutHours ?? 24) || 24,
          payoutMessage: String(
            data.payoutMessage ??
              data.PayoutMessage ??
              "Paid gifts are already sent to your payment account. Banks usually show them within 24 hours."
          ),
        };
      })
      .addCase(listFundingCampaigns.fulfilled, (state, action) => {
        state.list = apiData<any[]>(action.payload) || [];
      })
      .addCase(getFundingCampaign.fulfilled, (state, action) => {
        state.current = apiData(action.payload);
      })
      .addCase(listFundingCampaignContributions.fulfilled, (state, action) => {
        state.contributions = apiData<any[]>(action.payload) || [];
      })
      .addCase(createFundingCampaign.fulfilled, (state, action) => {
        const row = apiData(action.payload);
        if (row) {
          state.list = [row, ...state.list.filter((x) => (x.id || x.Id) !== (row.id || row.Id))];
        }
      })
      .addCase(requestFundingCampaignPayout.fulfilled, (state, action) => {
        const row = apiData(action.payload);
        if (row) state.current = row;
      })
      .addCase(appendFundingCampaignNote.fulfilled, (state, action) => {
        const row = apiData(action.payload);
        if (row) state.current = row;
      })
      .addCase(extendFundingCampaign.fulfilled, (state, action) => {
        const row = apiData(action.payload);
        if (row) state.current = row;
      })
      .addCase(updateFundingCampaignAmount.fulfilled, (state, action) => {
        const row = apiData(action.payload);
        if (row) state.current = row;
      });
  },
});

export default slice.reducer;
